<div class="wrapper flex flex-col justify-between">
  <div class="heading text-type-2 mb-[20px] flex justify-between items-center">
    <span>{{ isCreateMode ? 'New task' : task?.title }}</span>
  </div>

  @if (isCreateMode) {
    <!-- Create form (matches the create/edit warranty panel) -->
    <div class="details flex-1">
      <div class="row-details flex flex-col gap-[4px] mb-[20px]">
        <div class="label w-full">Milestone</div>
        <div class="value w-full text-type-11">{{ milestoneLabel }}</div>
      </div>
      <app-text-input
        [parentFormGroup]="taskForm"
        class="w-full mb-[20px]"
        controlName="title"
        label="task title"
      />
      <app-text-area
        [parentFormGroup]="taskForm"
        class="w-full mb-[20px]"
        controlName="description"
        label="details"
      />
      <app-date-picker
        [parentFormGroup]="taskForm"
        class="w-full mb-[20px]"
        controlName="due_date"
        label="due date"
      />
      <app-dropdown
        [enableSearch]="true"
        [options]="memberOptions"
        [parentFormGroup]="taskForm"
        class="w-full mb-[20px]"
        controlName="assigned_to"
        label="assign to"
        placeholder="Select"
      />
    </div>
    <div class="action-section w-full flex justify-between items-center gap-[10px] mt-[40px]">
      <app-primary-button (handleClick)="createTask()" [isDisabled]="!taskForm.valid" class="w-full">
        Create
      </app-primary-button>
    </div>
  } @else {
    <!-- Detail (matches the claim-detail panel) -->
    <mat-tab-group
      class="contents-tab-group flex-1"
      [selectedIndex]="selectedTabIndex"
      (selectedIndexChange)="selectedTabIndex = $event">
      <mat-tab label="Details">
        <div class="details-content pt-[20px]">
          <!-- Status -->
          <div class="row-details flex flex-col gap-[4px] mb-[20px]">
            <div class="label w-full">Status</div>
            <div class="value w-full">
              <div
                class="status-chip px-[14px] rounded-full w-fit"
                [ngClass]="statusMap[task?.status]?.chipClass || ''">
                {{ statusMap[task?.status]?.label || (task?.status | titlecase) }}
              </div>
            </div>
          </div>

          <!-- Warranty -->
          @if (warranty?.name) {
            <div class="row-details flex flex-col gap-[4px] mb-[20px]">
              <div class="label w-full">Warranty</div>
              <div class="value w-full text-type-11">{{ warranty.name }}</div>
            </div>
          }

          <!-- Milestone -->
          <div class="row-details flex flex-col gap-[4px] mb-[20px]">
            <div class="label w-full">Milestone</div>
            <div class="value w-full text-type-11">{{ milestoneLabel }}</div>
          </div>

          <!-- Assigned to -->
          <div class="row-details flex flex-col gap-[4px] mb-[20px]">
            <div class="label w-full">Assigned To</div>
            <div class="value w-full text-type-11">{{ assigneeName() }}</div>
          </div>

          <!-- Due date -->
          @if (task?.due_date) {
            <div class="row-details flex flex-col gap-[4px] mb-[20px]">
              <div class="label w-full">Due Date</div>
              <div class="value w-full text-type-11">{{ task.due_date | date:'MMM d, y' }}</div>
            </div>
          }

          <!-- Description -->
          @if (task?.description) {
            <div class="row-details flex flex-col gap-[4px] mb-[20px]">
              <div class="label w-full">Details</div>
              <div class="value w-full text-type-11">{{ task.description }}</div>
            </div>
          }

          <!-- Closing notes -->
          @if (task?.status === 'closed' && task?.closing_notes) {
            <div class="row-details flex flex-col gap-[4px] mb-[20px]">
              <div class="label w-full">Closing Notes</div>
              <div class="value w-full text-type-11">{{ task.closing_notes }}</div>
            </div>
          }

          <!-- Attachments -->
          @if (task?.attachments?.length) {
            <div class="row-details flex flex-col gap-[4px] mb-[20px]">
              <div class="label w-full">Attachments</div>
              <div class="pictures-row flex flex-wrap gap-[12px] mt-[8px]">
                @for (attachment of task.attachments; track attachment.id) {
                  <div class="picture-block">
                    @if (appService.getAttachmentFileType(attachment) === 'video') {
                      <div
                        (click)="appService.openCarousel($event, task.attachments, attachment, false)"
                        class="video-thumb"
                      >
                        @if (attachment.preview_gif_url || attachment.thumb_url) {
                          <img [src]="attachment.preview_gif_url || attachment.thumb_url" alt="Video preview" />
                        } @else if (attachment.original_url) {
                          <video [src]="attachment.original_url + '#t=0.1'" muted playsinline preload="metadata"></video>
                        }
                        <mat-icon class="play-icon">play_circle</mat-icon>
                      </div>
                    } @else {
                      <app-image
                        (click)="appService.openCarousel($event, task.attachments, attachment, false)"
                        [url]="attachment.thumb_url || attachment.original_url"
                        class="each-picture"
                      />
                    }
                  </div>
                }
              </div>
            </div>
          }

          <!-- Created by -->
          @if (task?.createdBy) {
            <div class="row-details flex flex-col gap-[4px] mb-[20px]">
              <div class="label w-full">Created By</div>
              <div class="value w-full text-type-11">
                {{ task.createdBy?.first_name }} {{ task.createdBy?.last_name }}
              </div>
            </div>
          }

          <!-- Actions dropdown -->
          @if (actionOptions.length) {
            <hr class="hr-line" />
            <app-dropdown
              [options]="actionOptions"
              [parentFormGroup]="actionForm"
              class="w-full mb-[20px]"
              controlName="action"
              placeholder="Take action on this task"
              label="actions"
            />

            @if (selectedAction === 'close') {
              <app-text-area
                [parentFormGroup]="actionForm"
                class="w-full mb-[20px]"
                controlName="notes"
                label="Comments"
              />
              <app-pictures-section-with-tags
                (onDelete)="deleteActionPicture($event)"
                [add]="true"
                [pictures]="actionMediaFiles"
                [tags]="'actions-attachments'"
                class="pictures-section mb-[40px]"
                label="Attachments"
              />
            }

            @if (selectedAction === 'reassign') {
              <app-dropdown
                [enableSearch]="true"
                [options]="memberOptions"
                [parentFormGroup]="actionForm"
                class="w-full mb-[20px]"
                controlName="assigned_to"
                label="assign to"
                placeholder="Select"
              />
            }
          }
        </div>
      </mat-tab>

      <mat-tab label="History">
        <div class="discussion-content pt-[20px]">
          @if (historyEntries.length) {
            @for (entry of historyEntries; track entry.uuid) {
              <div class="workflow-entry mb-[20px]">
                <div class="flex flex-row items-start gap-[12px]">
                  <div class="workflow-icon">
                    <mat-icon class="text-[#36AB19]">check_circle</mat-icon>
                  </div>
                  <div class="flex flex-col flex-1">
                    <div class="entry-action text-type-8">{{ entry.action }}</div>
                    <div class="flex flex-row items-center gap-[8px] mt-[8px]">
                      <div class="avatar">{{ entry.actor_initials }}</div>
                      <div class="flex flex-col">
                        <span class="text-type-11">{{ entry.actor_name }}</span>
                      </div>
                    </div>
                    <div class="text-type-14 text-[#60698B] mt-[4px]">
                      {{ entry.date | date:'MMM d, y' }} &bull; {{ entry.date | date:'h:mm a' }}
                    </div>
                    @if (entry.notes) {
                      <div class="comment-section mt-[12px]">
                        <div class="comment-label text-type-13 uppercase">Comment:</div>
                        <div class="comment-text text-type-11 mt-[4px]">{{ entry.notes }}</div>
                      </div>
                    }
                    @if (entry.attachments?.length) {
                      <div class="comment-section mt-[12px]">
                        <div class="comment-label text-type-13 uppercase">Media:</div>
                        <div class="pictures-row flex flex-wrap gap-[12px] mt-[8px]">
                          @for (attachment of entry.attachments; track attachment.id) {
                            <div class="picture-block">
                              @if (appService.getAttachmentFileType(attachment) === 'video') {
                                <div
                                  (click)="appService.openCarousel($event, entry.attachments, attachment, false)"
                                  class="video-thumb"
                                >
                                  @if (attachment.preview_gif_url || attachment.thumb_url) {
                                    <img [src]="attachment.preview_gif_url || attachment.thumb_url" alt="Video preview" />
                                  } @else if (attachment.original_url) {
                                    <video [src]="attachment.original_url + '#t=0.1'" muted playsinline preload="metadata"></video>
                                  }
                                  <mat-icon class="play-icon">play_circle</mat-icon>
                                </div>
                              } @else {
                                <app-image
                                  (click)="appService.openCarousel($event, entry.attachments, attachment, false)"
                                  [url]="attachment.thumb_url || attachment.original_url"
                                  class="each-picture"
                                />
                              }
                            </div>
                          }
                        </div>
                      </div>
                    }
                  </div>
                </div>
                <hr class="entry-divider" />
              </div>
            }
          } @else {
            <div class="no-data text-type-14 text-[#60698B] text-center py-[40px]">No activity yet.</div>
          }
        </div>
      </mat-tab>
    </mat-tab-group>

    <!-- Submit button at bottom -->
    @if (actionOptions.length && selectedTabIndex === 0) {
      <div class="action-section mb-[5px] flex flex-row gap-[20px] w-full">
        <app-primary-button (handleClick)="executeAction()" [isDisabled]="!canSubmitAction" class="flex-1">
          Submit
        </app-primary-button>
      </div>
    }
  }
</div>
